import { readStamped, writeStamped, safeRemoveItem, clearStampedForOwner } from './activeRoleStorage';

/**
 * The "how this works" notes (issue 994) that a person has put away.
 *
 * Browser-local by design, like the demo bar's first-visit line
 * (`demoStageIntro.ts`, whose shape this copies): no server preference, no
 * telemetry, another device shows the note once more.
 *
 * ONE stamped value per workspace holds the whole set of dismissed note ids, so a
 * new note costs no new key and the two "forget" hooks in `workspaceLocalState`
 * have exactly one pair of keys to drop. Stamped per user id (never the e-mail,
 * which changes) and keyed per workspace: a colleague who signs in on the same
 * browser meets the note, and a user who signs out and back in does not.
 *
 * KNOWN LIMIT, accepted: with one stamped slot per workspace the stamp belongs to
 * the LAST reader -- `readStamped` drops value and stamp when a different user reads
 * them -- so rendering a note as user B wipes user A's dismissals for that workspace,
 * and two people alternating in one browser see the notes again. That is the demo
 * bar's behaviour too; per-user keys would avoid it but break the one-key-pair shape
 * the two forget hooks rely on.
 *
 * The ids are plain strings the host passes ('time.approvals', …); this module
 * holds no list of them, so no app depends on another app's catalogue.
 */
const VALUE_PREFIX = 'explainNotesDismissed:';
const OWNER_PREFIX = 'explainNotesDismissedOwner:';

function keysFor(tenantSlug: string | null | undefined): { valueKey: string; ownerKey: string } | null {
  const slug = String(tenantSlug || '').trim();
  if (!slug) return null;
  return { valueKey: `${VALUE_PREFIX}${slug}`, ownerKey: `${OWNER_PREFIX}${slug}` };
}

function readIds(keys: { valueKey: string; ownerKey: string }, userId: string): string[] {
  const raw = readStamped(keys.valueKey, keys.ownerKey, userId);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    // A value this module did not write reads as "nothing dismissed".
    return [];
  }
}

export function explainNoteDismissed(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  noteId: string,
): boolean {
  const keys = keysFor(tenantSlug);
  if (!userId || !keys || !noteId) return false;
  return readIds(keys, userId).includes(noteId);
}

/**
 * Remember the dismissal. Fails open on purpose: without a workspace or an owner
 * to stamp it with there is nothing to persist, and the caller has already closed
 * the note for this tab either way.
 */
export function dismissExplainNote(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  noteId: string,
): void {
  const keys = keysFor(tenantSlug);
  if (!userId || !keys || !noteId) return;
  const ids = readIds(keys, userId);
  if (ids.includes(noteId)) return;
  writeStamped(keys.valueKey, keys.ownerKey, userId, JSON.stringify([...ids, noteId]));
}

/** Forget every dismissal, for every account in this browser — the workspace itself is gone. */
export function clearExplainNoteDismissals(tenantSlug: string | null | undefined): void {
  const keys = keysFor(tenantSlug);
  if (!keys) return;
  safeRemoveItem(keys.valueKey);
  safeRemoveItem(keys.ownerKey);
}

/** Forget one account's dismissals only — a colleague's stay. */
export function clearExplainNoteDismissalsForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const keys = keysFor(tenantSlug);
  if (!keys) return;
  clearStampedForOwner(keys.valueKey, keys.ownerKey, userId);
}
